import { reportDraftDb } from "../report-draft/reportDraftDb";
import {
	appendTurnEvent,
	clearCancelledTurns,
	clearDroppedTurnOps,
	connectionSnapshot,
	currentDrainTrigger,
	readCancelledTurns,
	readDroppedTurnOps,
	recordDroppedTurnOp,
} from "./turnEvents";

// Motor de drenagem da fila offline do assistente. PORTÁVEL: sem window/DOM,
// recebe dependências por argumento, corre igual em página e Service Worker.

// 408/429 são transitórios por definição (timeout / throttle do Laravel — o
// burst de reconnect após uma manhã offline atinge os buckets assistant-ai e
// assistant-write): nunca terminais, ficam na fila para o próximo gatilho.
const TRANSIENT_STATUSES = new Set([408, 429]);

// O PUT ao S3 pode devolver 4xx transitório (400 RequestTimeout num upload que
// estala em rede fraca, 403 de presign expirado) — e cada tentativa gera um
// presign NOVO, logo re-tentar resolve. O cap evita loop infinito num 403
// persistente de policy.
const MAX_S3_PUT_ATTEMPTS = 5;

async function post(url, body, getCsrf) {
	const csrf = getCsrf ? await getCsrf() : null;
	const res = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
			"X-Requested-With": "XMLHttpRequest",
			...(csrf ? { "X-CSRF-TOKEN": csrf } : {}),
		},
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		throw { status: res.status };
	}
	return res.json();
}

async function uploadPhoto(op, { apiBaseFor, getCsrf }) {
	const photo = await reportDraftDb.getPhoto(op.photoUuid);
	if (!photo) {
		return;
	}
	const apiBase = apiBaseFor(op.visitId);
	const bytes = photo.blob && typeof photo.blob.size === "number" ? photo.blob.size : null;

	const t0 = Date.now();
	const presign = await post(`${apiBase}/offline-photos/presign`, { photo_uuids: [op.photoUuid] }, getCsrf);
	await appendTurnEvent(op.turnUuid, "presign", { photoUuid: op.photoUuid, ms: Date.now() - t0 });

	const upload = presign.uploads[0];
	const t1 = Date.now();
	const put = await fetch(upload.url, { method: "PUT", body: photo.blob, headers: upload.headers || {} });
	await appendTurnEvent(op.turnUuid, "put_s3", {
		photoUuid: op.photoUuid,
		ms: Date.now() - t1,
		bytes,
		status: put.status,
		attempt: (photo.s3Attempts || 0) + 1,
	});
	if (!put.ok) {
		throw { status: put.status, s3: true };
	}

	const t2 = Date.now();
	await post(`${apiBase}/offline-photos/confirm`, { photo_uuids: [op.photoUuid] }, getCsrf);
	await appendTurnEvent(op.turnUuid, "confirm", { photoUuid: op.photoUuid, ms: Date.now() - t2 });

	await reportDraftDb.deletePhoto(op.photoUuid);
}

/**
 * Resumo dos OUTROS turnos desta visita que ainda não chegaram ao servidor.
 *
 * Um turno preso no dispositivo é invisível do lado do servidor — é exatamente
 * o caso que o GPV descreve como "as fotos desapareceram". Como este POST já
 * está a acontecer, leva de boleia o estado dos que ficaram para trás, sem
 * gastar um pedido novo (BEL-1751).
 *
 * Cap de 10: acima disso já não é diagnóstico, é sintoma óbvio.
 *
 * @returns {Promise<Array<{uuid: string, photos: number, ageMs: number|null, syncState: string}>>}
 */
async function pendingTurnsSummary(visitId, currentTurnUuid) {
	try {
		const all = await reportDraftDb.allTurns();
		const agora = Date.now();

		return all
			.filter(
				t => t && String(t.visitId) === String(visitId) && t.turnUuid !== currentTurnUuid && t.syncState !== "confirmed"
			)
			.slice(0, 10)
			.map(t => {
				const capturado = Date.parse(t.capturedAt);
				return {
					uuid: t.turnUuid,
					photos: Array.isArray(t.photoUuids) ? t.photoUuids.length : 0,
					ageMs: Number.isFinite(capturado) ? agora - capturado : null,
					syncState: t.syncState || null,
				};
			});
	} catch (e) {
		return [];
	}
}

async function postTurn(op, { apiBaseFor, getCsrf }) {
	const turn = await reportDraftDb.getTurn(op.turnUuid);
	if (!turn) {
		// Turn record gone (deleted by another execution — the restore race,
		// BEL-1751): discarding WITHOUT a trace would hide exactly the loss
		// this ticket is hunting. Record before the op leaves the queue; it
		// rides along the next confirmed turn POST of this visit.
		await recordDroppedTurnOp(op.visitId, op.turnUuid);
		return;
	}
	// Envia só as fotos confirmadas: exclui as marcadas "failed" (mantém
	// turn.photoUuids original no store para o progresso por-foto na bolha).
	const validUuids = [];
	for (const uuid of turn.photoUuids) {
		const photo = await reportDraftDb.getPhoto(uuid);
		if (photo && photo.syncState === "failed") {
			continue;
		}
		validUuids.push(uuid);
	}
	// Todas as fotos falharam: o servidor exige photo_uuids min:1, logo o POST
	// seria um 422 garantido — marca needs_attention localmente (bolha "precisa
	// de atenção" com retry) sem gastar uma chamada do bucket de throttle.
	if (turn.photoUuids.length > 0 && validUuids.length === 0) {
		turn.syncState = "needs_attention";
		await reportDraftDb.putTurn(turn);
		return;
	}
	// Fotos deste turno ainda por subir: o POST só pode levar 409 (o servidor
	// exige o lote completo em S3) e volta a ser tentado no gatilho seguinte, à
	// custa de uma chamada ao bucket de throttle. Acontece quando dois drains
	// correm ao mesmo tempo — a página e o Service Worker — e o segundo chega
	// aos turnos enquanto o primeiro ainda está a fazer upload das fotos. Só
	// espera enquanto o upload está mesmo agendado: uma foto sem op na fila
	// nunca mais subiria, e aí o turno segue (e o servidor decide).
	const aCaminho = new Set(
		(await reportDraftDb.allQueue())
			.filter(o => o.type === "photo_upload" && o.turnUuid === turn.turnUuid)
			.map(o => o.photoUuid)
	);
	if (validUuids.some(uuid => aCaminho.has(uuid))) {
		await appendTurnEvent(turn.turnUuid, "turn_wait", { pending_photos: aCaminho.size });

		return false; // adiado: a op fica na fila para o gatilho seguinte
	}
	const apiBase = apiBaseFor(op.visitId);
	const pendentes = await pendingTurnsSummary(op.visitId, turn.turnUuid);
	const cancelados = await readCancelledTurns(op.visitId);
	const droppedOps = await readDroppedTurnOps(op.visitId);
	const t0 = Date.now();
	const res = await post(
		`${apiBase}/offline-turns`,
		{
			client_uuid: turn.turnUuid,
			photo_uuids: validUuids,
			captured_photo_count: turn.photoUuids.length,
			text: turn.text,
			geo: turn.geo,
			device_meta: {
				...(turn.deviceMeta || {}),
				ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
				conn: connectionSnapshot(),
				// A cauda do registo: o turno já não existirá localmente depois
				// de confirmado, por isso esta é a única cópia que sobrevive.
				events: Array.isArray(turn.events) ? turn.events : [],
				pending_turns: pendentes,
				cancelled_turns: cancelados,
				dropped_turn_ops: droppedOps,
			},
			captured_at: turn.capturedAt,
		},
		getCsrf
	);
	await appendTurnEvent(turn.turnUuid, "turn_post", { ms: Date.now() - t0, ok: true });
	// Só agora: se o POST tivesse falhado, a op ficava na fila para retentar e
	// descartar aqui perdia os cancelamentos para sempre.
	await clearCancelledTurns(op.visitId);
	await clearDroppedTurnOps(op.visitId);
	turn.syncState = "confirmed";
	turn.taskId = res.task_id;
	turn.userMessageId = res.user_message_id;
	await reportDraftDb.putTurn(turn);
}

async function runPool(items, limit, worker) {
	const queue = [...items];
	const size = Math.max(1, limit);
	const runners = Array.from({ length: size }, async () => {
		while (queue.length) {
			await worker(queue.shift());
		}
	});
	await Promise.all(runners);
}

async function processOp(op, deps) {
	try {
		if (op.type === "photo_upload") {
			await uploadPhoto(op, deps);
		} else if (op.type === "assistant_turn") {
			// `false` = adiado (as fotos do turno ainda estão na fila). Tirar a
			// op aqui era transformar uma espera de segundos num turno que nunca
			// mais sobe — a avaria que este ticket anda a corrigir.
			if ((await postTurn(op, deps)) === false) {
				return;
			}
		}
		await reportDraftDb.dequeue(op.id);
	} catch (e) {
		if (e && e.status === 409 && op.type === "assistant_turn") {
			return; // batch incompleto: retentável
		}
		if (e && (e.status === 401 || e.status === 419)) {
			// Sessão expirada / token velho: NÃO é terminal. Fica na fila para
			// re-tentar após re-auth; sinaliza ao chamador (página) para mostrar
			// o modal de sessão. O SW omite onAuthError (drena no próximo abrir).
			if (typeof deps.onAuthError === "function") {
				deps.onAuthError();
			}
			return;
		}
		if (e && TRANSIENT_STATUSES.has(e.status)) {
			return; // throttle/timeout: fica na fila para o próximo gatilho
		}
		if (e && e.s3 && e.status >= 400 && e.status < 500) {
			if ((await bumpS3PutAttempts(op)) < MAX_S3_PUT_ATTEMPTS) {
				return; // presign novo a cada tentativa — retentável
			}
			await markPhotoFailed(op);
			await reportDraftDb.dequeue(op.id);
			return;
		}
		if (e && e.status >= 400 && e.status < 500) {
			if (op.type === "photo_upload") {
				await markPhotoFailed(op); // falha SÓ a foto; o turno segue
			} else {
				await markTurnNeedsAttention(op); // turno 4xx terminal — visível na UI
			}
			await reportDraftDb.dequeue(op.id);
		}
		// 5xx/rede: deixa na fila
	}
}

async function markTurnNeedsAttention(op) {
	if (op.type === "assistant_turn") {
		const turn = await reportDraftDb.getTurn(op.turnUuid);
		if (turn) {
			turn.syncState = "needs_attention";
			await reportDraftDb.putTurn(turn);
		}
	}
}

// Contador persistido NA FOTO (escrita atómica de 1 registo, sem API nova na
// fila). Foto desaparecida conta como esgotado — o markPhotoFailed a seguir é
// no-op e a op sai da fila.
async function bumpS3PutAttempts(op) {
	const photo = await reportDraftDb.getPhoto(op.photoUuid);
	if (!photo) {
		return MAX_S3_PUT_ATTEMPTS;
	}
	photo.s3Attempts = (photo.s3Attempts || 0) + 1;
	await reportDraftDb.putPhoto(photo);
	return photo.s3Attempts;
}

async function markPhotoFailed(op) {
	// Marca SÓ a foto como falhada (escrita atómica de 1 registo — sem corrida).
	// NÃO mexe em turn.photoUuids: o turno mantém a lista ORIGINAL (para a bolha
	// mostrar o ✗ mesmo após reload); o postTurn filtra as falhadas no envio.
	const photo = await reportDraftDb.getPhoto(op.photoUuid);
	if (photo) {
		photo.syncState = "failed";
		await reportDraftDb.putPhoto(photo);
		await appendTurnEvent(op.turnUuid, "photo_failed", {
			photoUuid: op.photoUuid,
			attempts: (photo && photo.s3Attempts) || 0,
		});
	}
}

export async function drainAssistantQueue({ apiBaseFor, getCsrf, concurrency = 2, onAuthError }) {
	if (!navigator.onLine) {
		return (await reportDraftDb.allQueue()).length;
	}
	const all = (await reportDraftDb.allQueue()).filter(op => op.kind === "assistant");
	const photos = all.filter(op => op.type === "photo_upload").sort((a, b) => a.id - b.id);
	const turns = all.filter(op => op.type === "assistant_turn").sort((a, b) => a.id - b.id);
	const deps = { apiBaseFor, getCsrf, onAuthError };

	for (const op of turns) {
		await appendTurnEvent(op.turnUuid, "drain_start", {
			trigger: currentDrainTrigger(),
			queued_photos: photos.length,
			...connectionSnapshot(),
		});
	}

	// Fotos em paralelo (até `concurrency`); turnos depois (barreira de batch).
	await runPool(photos, concurrency, op => processOp(op, deps));
	for (const op of turns) {
		await processOp(op, deps);
	}

	return (await reportDraftDb.allQueue()).length;
}
